// src/pages/admin/SalesReportPage.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminReportsApi } from '@/api/services'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { TrendingUp, ShoppingBag, DollarSign, Package } from 'lucide-react'

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`
const COLORS = ['#FF3D7F', '#FF8500', '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']

function StatCard({ icon: Icon, label, value, sub, color = 'text-pink-500' }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-gray-50 ${color}`}><Icon className="w-5 h-5" /></div>
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  )
}

export default function SalesReportPage() {
  const today = new Date().toISOString().split('T')[0]
  const monthStart = today.slice(0, 8) + '01'
  const [from, setFrom] = useState(monthStart)
  const [to, setTo] = useState(today)

  const { data, isLoading } = useQuery({
    queryKey: ['sales-report', from, to],
    queryFn: () => adminReportsApi.sales({ from, to }).then(r => r.data),
  })

  const report = data?.data
  const summary = report?.summary || {}
  const daily = report?.daily || []
  const topProducts = report?.top_products || []
  const topCategories = report?.top_categories || []
  const paymentMethods = report?.payment_methods || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Sales Report</h1>
        <div className="flex items-center gap-2 text-sm">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border rounded-lg px-3 py-1.5" />
          <span className="text-gray-400">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border rounded-lg px-3 py-1.5" />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading report...</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={ShoppingBag} label="Total Orders" value={summary.total_orders || 0}
              sub={`${summary.delivered || 0} delivered · ${summary.cancelled || 0} cancelled`} />
            <StatCard icon={DollarSign} label="Gross Revenue" value={fmt(summary.gross_revenue)}
              color="text-green-500" />
            <StatCard icon={TrendingUp} label="Net Revenue" value={fmt(summary.net_revenue)}
              sub="Excl. cancelled" color="text-blue-500" />
            <StatCard icon={Package} label="Avg Order Value" value={fmt(summary.avg_order_value)}
              color="text-purple-500" />
          </div>

          {/* Revenue chart */}
          {daily.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h2 className="font-bold text-sm text-gray-700 mb-4">Daily Revenue</h2>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} labelFormatter={l => `Date: ${l}`} />
                  <Line type="monotone" dataKey="revenue" stroke="#FF3D7F" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top Products */}
            <div className="bg-white rounded-xl border p-5">
              <h2 className="font-bold text-sm text-gray-700 mb-4">Top Products</h2>
              {topProducts.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No data</p>
              ) : (
                <div className="space-y-3">
                  {topProducts.slice(0, 8).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                        <span className="truncate text-gray-800">{p.product}</span>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-xs text-gray-500">{p.units} units</span>
                        <span className="font-bold text-gray-900">{fmt(p.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Categories */}
            <div className="bg-white rounded-xl border p-5">
              <h2 className="font-bold text-sm text-gray-700 mb-4">Revenue by Category</h2>
              {topCategories.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No data</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={topCategories} dataKey="revenue" nameKey="category" cx="50%" cy="50%"
                      outerRadius={80} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}>
                      {topCategories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Payment Methods */}
          {paymentMethods.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h2 className="font-bold text-sm text-gray-700 mb-4">Payment Methods</h2>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={paymentMethods}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="method" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Bar dataKey="revenue" fill="#FF3D7F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  )
}
